import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, Crown, ShieldCheck, FileLock2, KeyRound, Sparkles, CheckCircle2 } from "lucide-react";
import { SearchBar } from "@/components/site/SearchBar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useSessionUserId } from "@/hooks/use-portal-identity";
import { useIsVip } from "@/hooks/use-user-roles";
import { createSolicitudVip } from "@/lib/solicitudes.functions";
import { AuthDialog } from "@/components/site/AuthDialog";

export const Route = createFileRoute("/vip")({
  head: () => ({
    meta: [
      { title: "Bizaclia VIP — Acceso exclusivo a anuncios off-market" },
      {
        name: "description",
        content:
          "Portal Bizaclia VIP: anuncios off-market verificados, con documentación completa e información detallada del activo. Solo usuarios VIP verificados.",
      },
      { property: "og:title", content: "Bizaclia VIP — Anuncios off-market" },
      {
        property: "og:description",
        content:
          "Accede a oportunidades off-market con documentación lista. Solo usuarios VIP verificados.",
      },
      { property: "og:url", content: "/vip" },
    ],
    links: [{ rel: "canonical", href: "/vip" }],
  }),
  component: VipPage,
});

function VipPage() {
  const userId = useSessionUserId();
  const { isVip, loading: vipLoading } = useIsVip();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const demo = String(search.demo ?? "").toLowerCase() === "true";
  const [authOpen, setAuthOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [descripcion, setDescripcion] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (descripcion.trim().length < 10) {
      toast.error("Cuéntanos un poco más (mínimo 10 caracteres).");
      return;
    }
    setSubmitting(true);
    try {
      await createSolicitudVip({ data: { descripcion: descripcion.trim() } });
      try {
        const { trackConversion } = await import("@/lib/tracking");
        trackConversion("generate_lead", { userId, listingId: "vip-access" });
      } catch {
        // silencioso
      }
      toast.success("Solicitud enviada. Te contactaremos para verificar tu acceso VIP.");
      setOpen(false);
      setDescripcion("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo enviar la solicitud";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-amber-50/60 via-background to-background dark:from-amber-950/20">
        <div
          aria-hidden
          className="absolute inset-x-0 -top-32 h-[520px] bg-[radial-gradient(ellipse_at_top,theme(colors.amber.300/25%),transparent_60%)] pointer-events-none"
        />
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 pt-10 pb-16 sm:pt-14 sm:pb-20 relative">
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Volver al inicio
          </Link>

          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-100/60 px-3 py-1 text-xs font-semibold text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
              <Crown className="h-3.5 w-3.5" /> Bizaclia VIP
            </span>

            <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] max-w-4xl">
              Portal inmobiliario <span className="text-amber-600 dark:text-amber-400">off-market</span> exclusivo de negocios
            </h1>

            <p className="mt-5 text-lg text-muted-foreground max-w-2xl">
              Solo para usuarios VIP verificados.
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                Acceso verificado
              </span>
              <span className="hidden sm:inline text-border">•</span>
              <span className="inline-flex items-center gap-1.5">
                <FileLock2 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                Documentación completa
              </span>
              <span className="hidden sm:inline text-border">•</span>
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                Oportunidades únicas
              </span>
            </div>

            <div className="w-full mt-10">
              {demo ? (
                <>
                  <SearchBar basePath="/portal-vip" demo />
                  <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
                    Modo demo: se mostrarán únicamente los anuncios de demostración.
                  </p>
                  <Button size="lg" className="mt-5 bg-amber-600 hover:bg-amber-700 text-white" asChild>
                    <Link to="/mybizaclia" search={{ tab: "vip", wizard: true }}>
                      Publicar anuncio
                    </Link>
                  </Button>
                </>
              ) : userId && isVip ? (
                <>
                  <SearchBar basePath="/portal-vip" />
                  <p className="mt-3 text-xs text-muted-foreground">
                    Busca en el catálogo VIP. Solo se muestran anuncios off-market.
                  </p>
                  <Button size="lg" className="mt-5 bg-amber-600 hover:bg-amber-700 text-white" asChild>
                    <Link to="/mybizaclia" search={{ tab: "vip", wizard: true }}>
                      Publicar anuncio
                    </Link>
                  </Button>
                </>
              ) : userId && !isVip ? (
                <div className="rounded-xl border border-amber-400/40 bg-amber-50/60 dark:bg-amber-950/20 p-6 text-center">
                  <Crown className="mx-auto h-8 w-8 text-amber-600 dark:text-amber-400" />
                  <p className="mt-3 text-sm text-amber-900 dark:text-amber-200">
                    Tu cuenta no tiene acceso VIP. Solicita el acceso para usar el buscador.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border border-border bg-muted/50 p-6 text-center">
                  <KeyRound className="mx-auto h-8 w-8 text-muted-foreground" />
                  <p className="mt-3 text-sm text-muted-foreground">
                    Inicia sesión para acceder al buscador VIP.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setAuthOpen(true)}
                  >
                    Iniciar sesión
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="container mx-auto max-w-6xl px-4 sm:px-6 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">
          ¿Qué incluye el acceso VIP?
        </h2>
        <p className="mt-3 text-center text-muted-foreground max-w-2xl mx-auto">
          Una capa premium sobre Bizaclia, pensada para inversores, family offices y operadores
          que buscan oportunidades cerradas con información completa desde el primer minuto.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              icon: KeyRound,
              title: "Anuncios off-market",
              desc: "Activos que no se publican en el portal abierto, negociados en exclusiva.",
            },
            {
              icon: FileLock2,
              title: "Documentación lista",
              desc: "Licencias, contratos, planos y cuentas auditadas listos para revisar.",
            },
            {
              icon: ShieldCheck,
              title: "Información verificada",
              desc: "Datos del activo confirmados por nuestro equipo antes de publicarse.",
            },
            {
              icon: Crown,
              title: "Trato preferente",
              desc: "Un gestor dedicado coordina visitas, ofertas y due diligence.",
            },
            {
              icon: Sparkles,
              title: "Alertas anticipadas",
              desc: "Avisos antes de que la oportunidad llegue al mercado abierto.",
            },
            {
              icon: CheckCircle2,
              title: "Compradores verificados",
              desc: "Solo perfiles validados pueden contactar con los vendedores.",
            },
          ].map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="rounded-xl border border-border bg-background p-5 hover:border-amber-400/50 transition-colors"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-semibold">{title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Cómo funciona */}
      <section className="border-t border-border bg-muted/30">
        <div className="container mx-auto max-w-5xl px-4 sm:px-6 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">
            Cómo conseguir el acceso VIP
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: "1",
                title: "Solicita acceso",
                desc: "Cuéntanos qué tipo de operador eres y qué tipo de activos buscas.",
              },
              {
                step: "2",
                title: "Verificación",
                desc: "Validamos tu perfil. Suele tardar 24–48 horas laborables.",
              },
              {
                step: "3",
                title: "Acceso al portal",
                desc: "Recibes credenciales y empiezas a ver el catálogo off-market.",
              },
            ].map((s) => (
              <div key={s.step} className="rounded-xl border border-border bg-background p-6">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-600 text-white font-bold">
                  {s.step}
                </div>
                <h3 className="mt-4 font-semibold">{s.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA solicitud */}
      <section className="container mx-auto max-w-4xl px-4 sm:px-6 py-16">
        <div className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-50 to-background p-8 sm:p-10 text-center dark:from-amber-950/20">
          <Crown className="mx-auto h-10 w-10 text-amber-600 dark:text-amber-400" />
          <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight">
            Solicita tu acceso VIP
          </h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Plazas limitadas. Cuéntanos brevemente tu perfil y qué buscas. Te contactaremos
            tras verificar tu solicitud.
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {userId ? (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">
                    <Crown className="h-4 w-4" /> Solicitar acceso VIP
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <form onSubmit={onSubmit}>
                    <DialogHeader>
                      <DialogTitle>Solicitud de acceso VIP</DialogTitle>
                      <DialogDescription>
                        Descríbenos qué tipo de VIP eres (inversor, family office, fondo,
                        operador, franquicia…) y qué oportunidades buscas.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <Textarea
                        autoFocus
                        rows={6}
                        placeholder="Ej. Family office con presencia en Madrid y Barcelona. Buscamos locales en planta calle con renta consolidada, ticket 500k–3M €."
                        value={descripcion}
                        onChange={(e) => setDescripcion(e.target.value)}
                        maxLength={4000}
                      />
                      <p className="mt-2 text-xs text-muted-foreground text-right">
                        {descripcion.length}/4000
                      </p>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setOpen(false)}
                        disabled={submitting}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        {submitting ? "Enviando..." : "Enviar solicitud"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            ) : (
              <>
                <Button
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => setAuthOpen(true)}
                >
                  <Crown className="h-4 w-4" /> Inicia sesión para solicitar
                </Button>
                <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
              </>
            )}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Al enviar aceptas que un gestor de Bizaclia te contacte para verificar tu perfil.
          </p>
        </div>
      </section>
    </main>
  );
}
